/**
 * Pure helpers of the client review page (spec #330, corrected by #334 to the real
 * aditor-media share API). No DOM, no network.
 * @module web/review/logic
 */

export const MEDIA_API = "https://media.aditor.ai/api";

/** The token out of `/review/<token>`, null for any other path. */
export function reviewTokenFromPath(pathname: string): string | null {
  const m = /^\/review\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  return m ? (m[1] ?? null) : null;
}

/** A stream URL the API returns relative (`/stream/hls/...`) resolves against the API base: the bare host is the web app. */
export function resolveStreamUrl(url: string, api: string = MEDIA_API): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  return `${api}${url.startsWith("/") ? "" : "/"}${url}`;
}

export function formatTimecode(seconds: number): string {
  const s = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const m = Math.floor(s / 60);
  const rest = Math.floor(s % 60);
  return `${m}:${String(rest).padStart(2, "0")}`;
}

export interface ShareComment {
  id: string;
  body: string;
  timecode: number | null;
  parentId: string | null;
  author: string;
  createdAt: string | null;
  /** The API's `annotation` object: `{drawing_data, frame_number}`. */
  annotation: unknown;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v ? v : null;
}

/** Anything marked internal, and anything the automated reviewer wrote (`review_source` set), never reaches a client. */
export function isInternalComment(raw: Record<string, unknown>): boolean {
  if (String(raw.visibility ?? "").toLowerCase() === "internal") return true;
  if (raw.review_source !== undefined && raw.review_source !== null && raw.review_source !== "") return true;
  return false;
}

function authorOf(raw: Record<string, unknown>): string {
  const name = (v: unknown): string | null => (v && typeof v === "object" ? str((v as Record<string, unknown>).name) : null);
  return str(raw.author_label) ?? name(raw.author) ?? name(raw.guest_author) ?? "Client";
}

/** Flatten the comment tree (replies arrive nested), drop internal ones, keep order. */
export function normalizeComments(list: unknown): ShareComment[] {
  const out: ShareComment[] = [];
  const walk = (items: unknown, parent: string | null): void => {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      const raw = item as Record<string, unknown>;
      if (isInternalComment(raw)) continue;
      const id = str(raw.id);
      if (!id) continue;
      const tc = raw.timecode_start;
      out.push({
        id,
        body: String(raw.body ?? ""),
        timecode: typeof tc === "number" && Number.isFinite(tc) ? tc : null,
        parentId: str(raw.parent_id) ?? parent,
        author: authorOf(raw),
        createdAt: str(raw.created_at),
        annotation: raw.annotation ?? null,
      });
      walk(raw.replies, id);
    }
  };
  walk(list, null);
  return out;
}

/** One marker per top-level timed comment, position as a 0..1 fraction of the duration. */
export function commentMarkers(comments: ShareComment[], duration: number): Array<{ id: string; at: number; fraction: number }> {
  if (!(duration > 0)) return [];
  return comments
    .filter((c) => c.parentId === null && c.timecode !== null)
    .map((c) => ({ id: c.id, at: c.timecode as number, fraction: Math.min(1, Math.max(0, (c.timecode as number) / duration)) }));
}

/** A fresh key per composed comment; a retry of the SAME comment reuses it. */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** What a link allows: `ShareLinkValidateResponse` of the media API. */
export interface LinkInfo {
  title: string;
  assetId: string | null;
  allowDownload: boolean;
  canComment: boolean;
  showVersions: boolean;
  requiresPassword: boolean;
  /** A secure link: needs an Aditor login, the page can never open it. */
  requiresAuth: boolean;
  /** Folder or project link; a single-file link (asset_id only) opens view B directly. */
  isFolder: boolean;
  shareSession: string | null;
}

export function normalizeLink(raw: Record<string, unknown>): LinkInfo {
  const permission = String(raw.permission ?? "view").toLowerCase();
  const assetId = str(raw.asset_id);
  const container = str(raw.folder_id) ?? str(raw.project_id);
  return {
    title: str(raw.title) ?? str(raw.folder_name) ?? str(raw.project_name) ?? "Review",
    assetId,
    allowDownload: raw.allow_download === true,
    canComment: permission !== "view",
    showVersions: raw.show_versions === true,
    requiresPassword: raw.requires_password === true,
    requiresAuth: raw.requires_auth === true,
    isFolder: !(assetId && !container),
    shareSession: str(raw.share_session),
  };
}
